import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import IndustryNotice from "../components/IndustryNotice";
import { ArrowRight, ArrowLeft, CheckCircle, Loader2, AlertTriangle, FileText, Upload, X, Paperclip } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INDUSTRIES = ["SaaS", "Healthcare", "Aviation", "Finance", "Marketplace", "E-commerce", "Education", "Other"];
const FEATURES = ["Authentication", "Dashboard", "Payments", "Messaging", "Admin Panel", "API Integrations", "AI Features", "Analytics"];
const LOOKS = ["Minimal", "Corporate", "Startup", "Luxury", "Custom"];
const PLATFORMS = ["Web App", "Mobile Responsive", "API Backend Only"];

const StepIndicator = ({ current, total }) => (
  <div className="flex items-center justify-center gap-2 mb-8">
    {Array.from({ length: total }, (_, i) => i + 1).map(s => (
      <div key={s} className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all
          ${s < current ? "bg-zinc-950 border-zinc-950 text-white" : s === current ? "border-zinc-950 text-zinc-950" : "border-zinc-300 text-zinc-400"}`}>
          {s < current ? <CheckCircle size={14} /> : s}
        </div>
        {s < total && <div className={`h-0.5 w-8 ${s < current ? "bg-zinc-950" : "bg-zinc-200"}`} />}
      </div>
    ))}
  </div>
);

const FEATURE_KEYWORDS = [
  { name: "Authentication", tokens: ["login", "signup", "sign up", "account", "user", "auth", "password", "register"] },
  { name: "Payments", tokens: ["payment", "checkout", "stripe", "subscription", "billing", "invoice", "paid", "credit card"] },
  { name: "Dashboard", tokens: ["dashboard", "analytics", "chart", "metrics", "report", "kpi", "insight"] },
  { name: "Messaging", tokens: ["chat", "message", "notification", "inbox", "conversation", "dm", "email alert"] },
  { name: "Admin Panel", tokens: ["admin", "moderator", "back office", "manage users", "cms"] },
  { name: "API Integrations", tokens: ["api", "integration", "webhook", "third party", "connect to", "sync with"] },
  { name: "AI Features", tokens: ["ai ", "ml ", "gpt", "llm", "openai", "claude", "machine learning", "prediction", "recommendation"] },
  { name: "Search", tokens: ["search", "filter", "find", "browse", "discover", "explore"] },
  { name: "File Upload", tokens: ["upload", "attach", "file", "photo", "image", "document", "pdf"] },
  { name: "Maps / Location", tokens: ["map", "location", "geo", "address", "nearby", "route", "gps"] },
];

const POSTPONE_CANDIDATES = ["Referral program", "Loyalty rewards", "Native mobile apps", "Public API portal", "White-label theming", "Multi-language UI", "SSO / SAML"];

/**
 * Client-side, no-API heuristic that produces a preview scope from the
 * described idea + industry. The point is to give visitors real value
 * BEFORE they hand over contact details — an SEO / UX win for /build.
 */
function analyzeScope(desc, industry) {
  const text = (desc || "").toLowerCase();
  const words = text.split(/\s+/).filter(Boolean).length;
  const matched = FEATURE_KEYWORDS.filter(f => f.tokens.some(t => text.includes(t))).map(f => f.name);
  const industryBoost = ["Healthcare", "Finance", "Marketplace"].includes(industry) ? 1 : 0;
  const rawScore = Math.min(10, Math.round((words / 60) + matched.length + industryBoost * 2));
  const pkg = rawScore <= 2 ? { name: "Speed Build", weeks: 1, tagline: "Fastest working software" }
            : rawScore <= 4 ? { name: "Reviewed Build", weeks: 2, tagline: "AI speed with human polish" }
            : rawScore <= 7 ? { name: "Architected Build", weeks: 3, tagline: "Production-ready data foundations" }
            :                 { name: "Premium MVP", weeks: 4, tagline: "Ready for limited production release" };
  // Recommend the 3 most-relevant matches; suggest postponing 2 features we
  // did NOT detect (unlikely to matter for the MVP hypothesis).
  const recommend = matched.slice(0, 5).length ? matched.slice(0, 5) : ["Authentication", "Dashboard"];
  const postpone = POSTPONE_CANDIDATES.filter(p => !text.includes(p.toLowerCase().split(" ")[0])).slice(0, 3);
  return { recommend, postpone, pkg, score: rawScore };
}

function ScopePreview({ desc, industry }) {
  if (!desc || desc.trim().length < 40) return null;
  const { recommend, postpone, pkg, score } = analyzeScope(desc, industry);
  return (
    <div data-testid="scope-preview" className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-sky-50 p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">Preliminary scope preview</div>
          <div className="text-sm text-zinc-600 mt-0.5">Based on your description so far. Continue below for the exact locked quote.</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Recommended package</div>
          <div className="text-lg font-extrabold text-zinc-950" data-testid="preview-package">{pkg.name}</div>
          <div className="text-xs text-zinc-500">{pkg.weeks}-week delivery · {pkg.tagline}</div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-2">Likely to include</div>
          <ul className="space-y-1" data-testid="preview-recommend">
            {recommend.map(f => (
              <li key={f} className="text-sm text-zinc-700 flex items-start gap-2">
                <CheckCircle size={12} className="text-emerald-600 mt-1 flex-shrink-0" /> {f}
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Consider postponing for v1</div>
          <ul className="space-y-1" data-testid="preview-postpone">
            {postpone.map(p => (
              <li key={p} className="text-sm text-zinc-500 flex items-start gap-2">
                <span className="mt-1 text-zinc-400">•</span> {p}
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="text-[11px] text-zinc-500 mt-4 pt-3 border-t border-emerald-100">
        Preview based on a lightweight heuristic. Complexity score: {score}/10 · Fill in the fields below to get the exact locked price.
      </div>
    </div>
  );
}

const StepIdea = ({ data, onChange }) => (
  <div className="space-y-5" data-testid="step-idea">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-semibold text-zinc-700 mb-1.5">Your Name *</label>
        <input data-testid="input-client-name" value={data.clientName} onChange={e => onChange("clientName", e.target.value)}
          className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-zinc-900 focus:outline-none text-sm" placeholder="Jane Smith" />
      </div>
      <div>
        <label className="block text-sm font-semibold text-zinc-700 mb-1.5">Email Address *</label>
        <input data-testid="input-client-email" type="email" value={data.clientEmail} onChange={e => onChange("clientEmail", e.target.value)}
          className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-zinc-900 focus:outline-none text-sm" placeholder="jane@company.com" />
      </div>
    </div>
    <div>
      <label className="block text-sm font-semibold text-zinc-700 mb-1.5">Describe Your Idea *</label>
      <textarea data-testid="input-idea-description" value={data.ideaDescription} onChange={e => onChange("ideaDescription", e.target.value)} rows={5}
        className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-zinc-900 focus:outline-none text-sm resize-none"
        placeholder="Describe what you want to build. Be as detailed as possible — the AI will use this to generate your scope of work..." />
    </div>
    <div>
      <label className="block text-sm font-semibold text-zinc-700 mb-1.5">Industry *</label>
      <select data-testid="select-industry" value={data.industry} onChange={e => onChange("industry", e.target.value)}
        className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-zinc-900 focus:outline-none text-sm">
        <option value="">Select industry</option>
        {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
      </select>
    </div>
    <ScopePreview desc={data.ideaDescription} industry={data.industry} />
    {data.industry && <IndustryNotice industry={data.industry} />}
  </div>
);

const MAX_INTAKE_FILES = 5;
const MAX_INTAKE_BYTES = 15 * 1024 * 1024;

const StepFeatures = ({ data, onChange }) => {
  const addFiles = (fileList) => {
    const current = data.intakeFiles || [];
    const next = [...current];
    const errs = [];
    for (const f of Array.from(fileList || [])) {
      if (next.length >= MAX_INTAKE_FILES) { errs.push(`Max ${MAX_INTAKE_FILES} files`); break; }
      if (!/\.pdf$/i.test(f.name)) { errs.push(`"${f.name}" — only PDF files are accepted`); continue; }
      if (f.size > MAX_INTAKE_BYTES) { errs.push(`"${f.name}" is too large (max 15 MB)`); continue; }
      if (next.some((x) => x.name === f.name && x.size === f.size)) continue;
      next.push(f);
    }
    onChange("intakeFiles", next);
    onChange("intakeFilesError", errs.join(" · "));
  };

  const removeFile = (idx) => {
    const next = [...(data.intakeFiles || [])];
    next.splice(idx, 1);
    onChange("intakeFiles", next);
  };

  return (
    <div data-testid="step-features">
      <p className="text-zinc-600 text-sm mb-5">Select the core features your MVP needs. The AI will also identify additional components from your description.</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {FEATURES.map(f => {
          const selected = data.features.includes(f);
          return (
            <button key={f} data-testid={`feature-${f.toLowerCase().replace(/ /g, "-")}`}
              onClick={() => onChange("features", selected ? data.features.filter(x => x !== f) : [...data.features, f])}
              className={`px-4 py-3 rounded-xl border text-sm font-medium transition-all text-left ${selected ? "bg-zinc-950 border-zinc-950 text-white" : "bg-zinc-50 border-zinc-200 text-zinc-700 hover:border-zinc-400"}`}>
              {f}
            </button>
          );
        })}
      </div>

      <div className="bg-gradient-to-br from-blue-50 to-purple-50/60 border border-blue-200 rounded-2xl p-5" data-testid="intake-pdf-section">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-white border border-blue-200 flex items-center justify-center flex-shrink-0">
            <FileText size={16} className="text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-zinc-950 text-sm">Attach PDFs (optional)</div>
            <p className="text-xs text-zinc-600 mt-0.5 leading-relaxed">
              Have a spec, RFP, wireframe export, brand guidelines, or slide deck? Attach up to {MAX_INTAKE_FILES} PDFs (15 MB each). Our AI will read them and factor their contents into your fixed-price quote.
            </p>

            <label className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-white border border-zinc-200 rounded-full text-xs font-semibold text-zinc-900 hover:bg-zinc-50 cursor-pointer" data-testid="btn-add-intake-pdf">
              <Upload size={12} /> Choose PDF{data.intakeFiles?.length ? "s" : ""}
              <input type="file" accept=".pdf,application/pdf" multiple className="hidden"
                data-testid="input-intake-pdf"
                onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
            </label>

            {data.intakeFilesError && (
              <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-2 py-1" data-testid="intake-files-error">
                {data.intakeFilesError}
              </div>
            )}

            {data.intakeFiles && data.intakeFiles.length > 0 && (
              <ul className="mt-3 space-y-2" data-testid="intake-files-list">
                {data.intakeFiles.map((f, i) => (
                  <li key={`${f.name}-${f.size}-${i}`} data-testid={`intake-file-${i}`}
                    className="flex items-center gap-2 bg-white border border-zinc-200 rounded-xl px-3 py-2 text-xs">
                    <Paperclip size={12} className="text-zinc-500 flex-shrink-0" />
                    <span className="font-semibold text-zinc-900 truncate flex-1">{f.name}</span>
                    <span className="text-zinc-500 flex-shrink-0">{Math.round(f.size / 1024)} KB</span>
                    <button onClick={() => removeFile(i)} data-testid={`btn-remove-intake-${i}`}
                      className="p-1 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-lg"><X size={12} /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const StepDesignAndTerms = ({ data, onChange }) => (
  <div className="space-y-6" data-testid="step-design-terms">
    <div>
      <label className="block text-sm font-semibold text-zinc-700 mb-3">Look & Feel</label>
      <div className="flex flex-wrap gap-3">
        {LOOKS.map(l => (
          <button key={l} data-testid={`look-${l.toLowerCase()}`}
            onClick={() => onChange("lookFeel", l)}
            className={`px-5 py-2.5 rounded-full border text-sm font-medium transition-all ${data.lookFeel === l ? "bg-zinc-950 border-zinc-950 text-white" : "bg-white border-zinc-200 text-zinc-700 hover:border-zinc-400"}`}>
            {l}
          </button>
        ))}
      </div>
    </div>
    <div>
      <label className="block text-sm font-semibold text-zinc-700 mb-1">Target Platforms <span className="text-zinc-400 font-normal text-xs">(select at least one)</span></label>
      <div className="flex flex-wrap gap-3 mt-2">
        {PLATFORMS.map(p => {
          const selected = data.platforms.includes(p);
          return (
            <button key={p} data-testid={`platform-${p.toLowerCase().replace(/ /g, "-")}`}
              onClick={() => {
                if (selected && data.platforms.length === 1) return; // prevent deselecting last
                onChange("platforms", selected ? data.platforms.filter(x => x !== p) : [...data.platforms, p]);
              }}
              className={`px-5 py-2.5 rounded-full border text-sm font-medium transition-all ${selected ? "bg-zinc-950 border-zinc-950 text-white" : "bg-white border-zinc-200 text-zinc-700 hover:border-zinc-400"}`}>
              {p}
            </button>
          );
        })}
      </div>
    </div>
    <div>
      <label className="block text-sm font-semibold text-zinc-700 mb-3">Currency</label>
      <div className="flex gap-3">
        {["usd", "eur"].map(c => (
          <button key={c} data-testid={`currency-${c}`}
            onClick={() => onChange("currency", c)}
            className={`px-6 py-3 rounded-full border text-sm font-semibold transition-all ${data.currency === c ? "bg-zinc-950 border-zinc-950 text-white" : "bg-white border-zinc-200 text-zinc-700 hover:border-zinc-400"}`}>
            {c === "usd" ? "USD ($)" : "EUR (€)"}
          </button>
        ))}
      </div>
    </div>
    <div className="bg-zinc-50 rounded-xl p-4 border border-zinc-200">
      <div className="flex items-start gap-3">
        <input type="checkbox" id="accept-terms" data-testid="checkbox-accept-terms" checked={data.acceptTerms} onChange={e => onChange("acceptTerms", e.target.checked)} className="mt-0.5" />
        <label htmlFor="accept-terms" className="text-sm text-zinc-600">
          I agree to FastPrototype.ai's <a href="/legal#terms" className="underline text-zinc-900 hover:text-blue-600" target="_blank">Terms of Service</a>, <a href="/legal#nda" className="underline text-zinc-900 hover:text-blue-600" target="_blank">NDA</a>, and <a href="/legal#ip" className="underline text-zinc-900 hover:text-blue-600" target="_blank">IP Ownership Policy</a>. I acknowledge that client retains full IP ownership upon final payment, that FastPrototype.ai does not provide regulatory compliance certification, and that I am solely responsible for ensuring my software product complies with all applicable industry regulations. I have read the <a href="/legal#industry" className="underline text-zinc-900 hover:text-blue-600" target="_blank">Industry Regulatory Notices</a>.
        </label>
      </div>
    </div>
  </div>
);

const STEP_TITLES = ["Your Idea", "Core Features", "Design, Platforms & Terms"];
const STEP_SUBTITLES = ["Tell us about your project", "What does your MVP need?", "Pick style & agree to terms"];

export default function ProjectBuilder() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState({
    clientName: "", clientEmail: "", ideaDescription: "", industry: "",
    features: [], lookFeel: "Startup", platforms: ["Web App"],
    currency: "usd", acceptTerms: false,
    intakeFiles: [], intakeFilesError: "",
  });

  const onChange = (field, value) => setData(prev => ({ ...prev, [field]: value }));

  const canNext = () => {
    if (step === 1) return data.clientName && data.clientEmail && data.ideaDescription && data.industry;
    if (step === 2) return true;
    if (step === 3) return data.lookFeel && data.platforms.length >= 1 && data.acceptTerms;
    return true;
  };

  const handleSubmit = async () => {
    setLoading(true); setError("");
    try {
      const promoterCode = (typeof window !== "undefined" && window.localStorage)
        ? localStorage.getItem("lf_promoter_code") : null;
      const promoterClickId = (typeof window !== "undefined" && window.localStorage)
        ? localStorage.getItem("lf_promoter_click") : null;
      const res = await axios.post(`${API}/projects`, {
        client_name: data.clientName, client_email: data.clientEmail,
        idea_description: data.ideaDescription, industry: data.industry,
        features: data.features, look_feel: data.lookFeel,
        platforms: data.platforms, delivery_weeks: 3,
        currency: data.currency,
        promoter_code: promoterCode || undefined,
        promoter_click_id: promoterClickId || undefined,
      }, { withCredentials: true });
      const projectId = res.data.project_id;

      // Upload any attached PDFs before navigating so the proposal generator
      // will see them during its first call.
      const files = data.intakeFiles || [];
      if (files.length > 0) {
        for (const f of files) {
          const fd = new FormData();
          fd.append("email", data.clientEmail);
          fd.append("file", f);
          try {
            await axios.post(`${API}/projects/${projectId}/intake-files`, fd, {
              headers: { "Content-Type": "multipart/form-data" },
            });
          } catch (upErr) {
            // Keep going — a bad file shouldn't block the whole submit.
            // eslint-disable-next-line no-console
            console.warn(`Failed to upload ${f.name}:`, upErr);
          }
        }
      }

      if (typeof window !== "undefined" && window.lfTrack) {
        window.lfTrack("project_submitted", {
          project_id: projectId, industry: data.industry,
          intake_files: files.length,
        });
      }
      navigate(`/proposal/${projectId}`);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to submit project. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="project-builder-page">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 py-24">
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-zinc-950 mb-2">Build Your MVP</h1>
          <p className="text-zinc-500 text-sm">Step {step} of 3 — {STEP_SUBTITLES[step-1]}</p>
          <p className="text-xs text-zinc-400 mt-2">You'll pick your delivery speed on the next page after we generate your quote.</p>
        </div>
        <StepIndicator current={step} total={3} />

        <div className="bg-white border border-zinc-200 rounded-2xl p-8 shadow-sm">
          <h2 className="text-xl font-bold text-zinc-900 mb-6">{STEP_TITLES[step-1]}</h2>
          {step === 1 && <StepIdea data={data} onChange={onChange} />}
          {step === 2 && <StepFeatures data={data} onChange={onChange} />}
          {step === 3 && <StepDesignAndTerms data={data} onChange={onChange} />}
          {error && <div data-testid="submit-error" className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">{error}</div>}
          <div className="flex justify-between mt-8">
            {step > 1 ? (
              <button data-testid="btn-prev-step" onClick={() => setStep(s => s - 1)}
                className="inline-flex items-center px-5 py-2.5 text-sm font-semibold text-zinc-700 border border-zinc-200 rounded-full hover:bg-zinc-50 transition-colors">
                <ArrowLeft size={15} className="mr-1.5" /> Back
              </button>
            ) : <div />}
            {step < 3 ? (
              <button data-testid="btn-next-step" onClick={() => setStep(s => s + 1)} disabled={!canNext()}
                className="inline-flex items-center px-6 py-2.5 text-sm font-semibold text-white bg-zinc-950 rounded-full hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                Next <ArrowRight size={15} className="ml-1.5" />
              </button>
            ) : (
              <button data-testid="btn-submit-project" onClick={handleSubmit} disabled={!canNext() || loading}
                className="inline-flex items-center px-6 py-2.5 text-sm font-semibold text-white bg-zinc-950 rounded-full hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                {loading ? <><Loader2 size={15} className="mr-2 animate-spin" /> {data.intakeFiles?.length ? "Uploading & submitting…" : "Submitting..."}</> : <>Generate My Scope <ArrowRight size={15} className="ml-1.5" /></>}
              </button>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
